import { usePrevious } from '@sequencehq/utils'
import { BulkActions, BulkActionsAction } from '@sequencehq/core-components'
import { match } from 'ts-pattern'
import {
  BulkAction,
  BulkActionEntityConfig,
  BulkActionTitleContext,
  ZipDownloadStatus
} from './types'
import {
  allCompletedIds,
  allFailedIds,
  allIds,
  allSuccessfulIds,
  canCancelBulkAction
} from './utils'
import { useEffect, useRef } from 'react'
import { useRunInvalidationForMutation } from '@sequencehq/api'
import { useDownloadBulkActionZip } from './useDownloadBulkActionZip'

function statusForId(
  bulkAction: BulkAction,
  id: string
): BulkActionsAction['status'] {
  return match({
    isComplete: bulkAction.completedIds.includes(id),
    isErrored: bulkAction.failedIds.includes(id),
    isSkipped: bulkAction.skippedIds.includes(id)
  })
    .with({ isComplete: true }, () => 'success' as const)
    .with({ isErrored: true }, () => 'failure' as const)
    .with({ isSkipped: true }, () => 'skipped' as const)
    .otherwise(() => 'pending' as const)
}

/**
 * Builds the review-drill-down rows. With no `renderRow` (counts-only
 * consumers) titles are empty: the rows still drive the summary counts, but the
 * Review drill-down is hidden, so the empty titles are never shown.
 */
function buildActions<TEntity>(
  bulkAction: BulkAction,
  entities: Record<string, TEntity>,
  renderRow?: BulkActionEntityConfig<TEntity>['renderRow']
): BulkActionsAction[] {
  return allIds(bulkAction).map(id => ({
    id,
    title: renderRow ? renderRow(id, entities[id]) : '',
    status: statusForId(bulkAction, id)
  }))
}

export function titleForBulkAction(
  bulkAction: Pick<
    BulkAction,
    | 'type'
    | 'status'
    | 'requestedIds'
    | 'completedIds'
    | 'failedIds'
    | 'skippedIds'
  >,
  config: Pick<BulkActionEntityConfig, 'titles'>,
  zipStatus: ZipDownloadStatus = 'idle'
): string {
  // The zip phase is passed to the entity's title renderer via the context, so
  // download title copy lives in the entity config, not here.
  const ctx: BulkActionTitleContext = {
    status: bulkAction.status,
    singularAction: allIds(bulkAction).length === 1,
    totalSuccessful: allSuccessfulIds(bulkAction).length,
    totalFailed: allFailedIds(bulkAction).length,
    progress: `${allCompletedIds(bulkAction).length}/${allIds(bulkAction).length}`,
    zipStatus
  }

  return config.titles[bulkAction.type]?.(ctx) ?? ''
}

/**
 * Re-invalidate the entity cache as the async bulk action progresses, so the
 * underlying list reflects completed entities. The config's mutation determines
 * which caches are invalidated (via its `invalidateQueries`).
 */
const useInvalidateCacheOnProgress = (props: {
  bulkAction: BulkAction
  mutation: BulkActionEntityConfig['invalidateOnProgress']
}) => {
  const completedCount = allCompletedIds(props.bulkAction).length
  const runMutationInvalidation = useRunInvalidationForMutation()

  const previousCompletedCount = usePrevious(completedCount)

  useEffect(() => {
    if (completedCount > 0 && previousCompletedCount !== completedCount) {
      runMutationInvalidation(props.mutation)
    }
  }, [
    completedCount,
    runMutationInvalidation,
    previousCompletedCount,
    props.mutation
  ])
}

export const BulkActionInFlight = <TEntity,>({
  bulkAction,
  onDismiss,
  entities,
  config,
  isReviewing,
  onChangeReviewing,
  onCancel,
  isCancelling = false
}: {
  bulkAction: BulkAction
  onDismiss: () => void
  entities: Record<string, TEntity>
  config: BulkActionEntityConfig<TEntity>
  isReviewing: boolean
  onChangeReviewing: (isReviewing: boolean) => void
  onCancel?: (id: string) => void
  isCancelling?: boolean
}) => {
  const actions = buildActions(bulkAction, entities, config.renderRow)

  useInvalidateCacheOnProgress({
    bulkAction,
    mutation: config.invalidateOnProgress
  })

  const cancelAction = {
    available: canCancelBulkAction(bulkAction),
    handler: onCancel ? () => onCancel(bulkAction.id) : undefined,
    isRunning: isCancelling
  }

  // Save the zip automatically the moment a download finishes. We fire on the
  // PROCESSING -> terminal transition (not merely "is terminal") so we only grab
  // the zip for a download the user watched complete, never for one that was
  // already done when the panel loaded. A ref guards against a double fire (e.g.
  // StrictMode).
  const isDownload =
    config.downloadZip?.types.includes(bulkAction.type) ?? false
  const isTerminal = bulkAction.status !== 'PROCESSING'
  const hasZip = allSuccessfulIds(bulkAction).length > 0
  const { download: downloadZip, status: zipStatus } = useDownloadBulkActionZip(
    bulkAction.id,
    config.downloadZip
  )
  const previousStatus = usePrevious(bulkAction.status)
  const justCompleted = previousStatus === 'PROCESSING' && isTerminal
  const hasAutoDownloaded = useRef(false)

  useEffect(() => {
    if (isDownload && justCompleted && hasZip && !hasAutoDownloaded.current) {
      hasAutoDownloaded.current = true
      void downloadZip()
    }
  }, [isDownload, justCompleted, hasZip, downloadZip])

  // The zip fetch runs client-side once the server job is terminal, and its
  // phase drives the title (via the entity's title renderer). Treat the
  // completion render as 'preparing' too — before `zipStatus` has flipped — so
  // the completed title never flashes before the zip-phase title.
  const zipPhase: ZipDownloadStatus =
    zipStatus === 'idle' && isDownload && justCompleted && hasZip
      ? 'preparing'
      : zipStatus

  return (
    <BulkActions
      title={titleForBulkAction(bulkAction, config, zipPhase)}
      status={bulkAction.status}
      actions={actions}
      listTitle={config.listTitle}
      onDismiss={onDismiss}
      isReviewing={isReviewing}
      onChangeReviewing={onChangeReviewing}
      cancelAction={cancelAction}
      showReview={Boolean(config.renderRow)}
    />
  )
}
